import * as THREE from 'three';

const _worldPos = new THREE.Vector3();
const _localOffset = new THREE.Vector3();

/**
 * @param {THREE.Object3D} marker
 * @param {THREE.Camera} camera
 * @param {HTMLCanvasElement} canvas
 * @param {number} [beamHeight=16]
 * @returns {{ x: number; y: number; visible: boolean }}
 */
export function projectMarkerToScreen(marker, camera, canvas, beamHeight = 16) {
  marker.updateWorldMatrix(true, false);
  _localOffset.set(0, beamHeight * 0.35, 0);
  marker.localToWorld(_localOffset);
  _worldPos.copy(_localOffset);
  _worldPos.project(camera);

  const behindCamera = _worldPos.z > 1;
  const rect = canvas.getBoundingClientRect();
  const x = (_worldPos.x * 0.5 + 0.5) * rect.width + rect.left;
  const y = (-_worldPos.y * 0.5 + 0.5) * rect.height + rect.top;

  const offScreen =
    x < rect.left || x > rect.right || y < rect.top || y > rect.bottom;

  return {
    x,
    y,
    visible: !behindCamera && !offScreen,
  };
}

/**
 * @returns {number} Viewport Y of the venue header bottom edge, or 0 if absent.
 */
export function getVenueHeaderBottom() {
  const header = document.getElementById('venue-header');
  if (!header) return 0;
  return header.getBoundingClientRect().bottom;
}

/**
 * @param {{ x: number; y: number; visible: boolean }} screen
 * @param {{ width: number; height: number }} viewport
 * @param {{ headerBottom?: number }} [options]
 * @returns {{ left: number; top: number; visible: boolean }}
 */
export function computeOverlayPlacement(screen, viewport, options = {}) {
  const vw = viewport.width;
  const vh = viewport.height;
  const safePad = Math.max(24, Math.min(vw, vh) * 0.02);
  const headerGap = Math.max(16, Math.min(vw, vh) * 0.015);
  const headerBottom = options.headerBottom ?? 0;
  const offsetX = Math.max(48, Math.min(vw * 0.04, 120));
  const cardWidth =
    vw >= 1920
      ? Math.max(340, Math.min(vw * 0.25, 500))
      : Math.max(380, Math.min(vw * 0.28, 460));
  const profileBleed = Math.max(90, Math.min(vw * 0.08, 120));
  const cardHeight = Math.min(vh * 0.5, 480);
  const badgeHeight = Math.max(48, Math.min(vh * 0.06, 64));
  const stackGap = Math.max(12, Math.min(vh * 0.015, 20));
  const totalHeight = badgeHeight + stackGap + cardHeight;
  const cardCenterFromTop = badgeHeight + stackGap + cardHeight / 2;

  let left = screen.x + offsetX;
  // Top-edge position so the card (not the whole stack) centers on the marker.
  // Stable during the card height reveal — avoids translateY(-50%) drifting upward.
  let top = screen.y - cardCenterFromTop;

  const minLeft = safePad + profileBleed;
  const maxLeft = vw - cardWidth - safePad;
  left = Math.max(minLeft, Math.min(maxLeft, left));

  const minTop = Math.max(safePad, headerBottom + headerGap);
  const maxTop = vh - safePad - totalHeight;
  top = Math.max(minTop, Math.min(maxTop, top));

  return {
    left,
    top,
    visible: screen.visible,
  };
}

/**
 * @param {HTMLElement | null} mountEl
 * @param {{ left: number; top: number; visible: boolean }} placement
 */
export function applyHotspotOverlayPlacement(mountEl, placement) {
  if (!mountEl) return;
  mountEl.style.setProperty('--hotspot-left', `${placement.left}px`);
  mountEl.style.setProperty('--hotspot-top', `${placement.top}px`);
  mountEl.style.setProperty('--hotspot-visible', placement.visible ? '1' : '0');
}

/**
 * @param {HTMLElement | null} mountEl
 */
export function resetHotspotOverlayPlacement(mountEl) {
  if (!mountEl) return;
  mountEl.style.removeProperty('--hotspot-left');
  mountEl.style.removeProperty('--hotspot-top');
  mountEl.style.removeProperty('--hotspot-visible');
}
